import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RoomService } from '../room/room.service';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
  getFirestore,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  QueryLimitConstraint,
  QueryStartAtConstraint,
} from 'firebase/firestore';
import { Room } from 'src/app/model/room/room';
import { OperatorService } from '../operator/operator.service';

@Injectable({
  providedIn: 'root',
})
export class RoomListService {
  private roomService = inject(RoomService);
  private operatorService = inject(OperatorService);
  private db = getFirestore();

  private roomObservablesSubject = new BehaviorSubject<Observable<Room | null>[]>([]);
  private hasGotRoomsSubject = new BehaviorSubject<boolean | undefined>(undefined);
  private hasMoreRoomsSubject = new BehaviorSubject<boolean>(true);
  private roomIdsSubject = new BehaviorSubject<string[]>([]);
  private readonly pageSize = 20;
  private lastRoomDocument: QueryDocumentSnapshot<DocumentData> | undefined;
  private roomsMap = new Set<string>();

  roomObservables$ = this.roomObservablesSubject.asObservable();
  hasGotRooms$ = this.hasGotRoomsSubject.asObservable();
  hasMoreRooms$ = this.hasMoreRoomsSubject.asObservable();
  roomIds$ = this.roomIdsSubject.asObservable();

  addRoom(roomId: string, userId: string) {
    if (!this.roomsMap.has(roomId)) {
      const roomObservable = this.roomService.getObservable(roomId, userId);
      this.roomsMap = new Set<string>([roomId, ...this.roomsMap]);
      const currentRooms = this.roomObservablesSubject.getValue();
      this.roomObservablesSubject.next([roomObservable, ...currentRooms]);
      this.updateRoomIds();
    }
  }

  async moreRooms(userId: string) {
    if (OperatorService.IsDemo) {
      return;
    }

    // Create base query
    const roomsRef = collection(this.db, 'matches', userId, 'rooms');

    // Build query constraints
    const constraints: (
      | QueryFieldFilterConstraint
      | QueryOrderByConstraint
      | QueryLimitConstraint
      | QueryStartAtConstraint
    )[] = [
      where('gameId', '==', this.operatorService.gameId),
      orderBy('sortDate', 'desc'),
      limit(this.pageSize + 1),
    ];

    // Add startAfter if we have a last document
    if (this.lastRoomDocument) {
      constraints.push(startAfter(this.lastRoomDocument));
    }

    // Create and execute query
    const q = query(roomsRef, ...constraints);
    const querySnapshot = await getDocs(q);
    const rooms = querySnapshot.docs;

    if (this.hasGotRoomsSubject.value === undefined) {
      this.hasGotRoomsSubject.next(rooms.length > 0);
    }

    const hasMore = rooms.length > this.pageSize;
    this.hasMoreRoomsSubject.next(hasMore);

    const currentRooms = this.roomObservablesSubject.value;
    const newRooms = new Array<Observable<Room | null>>();

    for (let index = 0; index < rooms.length; index++) {
      const room = rooms[index];
      if (!this.roomsMap.has(room.id)) {
        const roomObservable = this.roomService.getObservable(room.id, userId);
        this.roomsMap.add(room.id);
        newRooms.push(roomObservable);
      }
    }

    const updatedRooms = [...currentRooms, ...newRooms];
    this.roomObservablesSubject.next(updatedRooms);
    this.updateRoomIds();

    if (hasMore) {
      // Store the second-to-last document for pagination
      this.lastRoomDocument = rooms[rooms.length - 2];
    } else if (rooms.length > 0) {
      // If no more pages, store the last document
      this.lastRoomDocument = rooms[rooms.length - 1];
    }

    return hasMore;
  }

  hasRooms(): boolean {
    return this.roomObservablesSubject.value.length > 0;
  }

  roomCount(): number {
    return this.roomObservablesSubject.value.length;
  }

  private updateRoomIds(): void {
    const roomIds = Array.from(this.roomsMap);
    this.roomIdsSubject.next(roomIds);
  }

  signOut(): void {
    this.roomObservablesSubject.next([]);
    this.hasGotRoomsSubject.next(undefined);
    this.hasMoreRoomsSubject.next(true);
    this.lastRoomDocument = undefined;
    this.roomsMap = new Set<string>();
  }
}
